import React, { useState } from 'react'
import styled from '@emotion/styled'
import { DEFAULT_FOCUS_STYLES, DEFAULT_THEME_COLORS } from 'css/theme'
import { UiIcon } from '../Icon'

export interface CollapsibleProps extends React.ComponentPropsWithRef<'div'> {
  disabled?: boolean
  iconName?: string
  label: string
  dataId?: string
  defaultState?: boolean
  color?: string
  onCollapsibleToggle?: (isOpen: boolean) => void
}

type ListItemContainerProps = {
  disabled?: boolean
  color?: string
}

const ContainerExternal = styled.li<ListItemContainerProps>(({ color }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '76px',
  padding: '16px 24px',
  border: '1px solid',
  borderColor: color || DEFAULT_THEME_COLORS['--theme-color--neutral-80'],
  borderRadius: '8px',
  '&:focus-within': {
    borderRadius: '8px',
    outline: color || '2px #326CDB dotted',
    outlineOffset: '2px',
  },
  '&:hover': {
    borderColor: color || DEFAULT_THEME_COLORS['--theme-color--neutral-70'],
    boxShadow: '0px 0px 0px 2px rgba(0, 0, 0, 0.12)',
    cursor: 'pointer',
  },
  '&:focus': {
    outline: color || DEFAULT_FOCUS_STYLES['--theme--focus-outline'],
    outlineOffset: color || DEFAULT_FOCUS_STYLES['--theme--focus-outline-offset'],
  },
}))

const ContainerInternal = styled.div({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',
  height: '40px',
  gap: '12px',
})

const Text = styled.div({
  display: 'flex',
  flexDirection: 'column',
  marginRight: '12px',
  width: '100%',
})

const Label = styled.span({
  fontWeight: 400,
  fontSize: '16px',
  color: DEFAULT_THEME_COLORS['--theme-color--neutral-10'],
})

const Chevron = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  flexGrow: 1,
})

export const Collapsible = (props: CollapsibleProps) => {
  const { disabled, iconName, label, children, dataId = '', color, defaultState } = props

  const [isOpen, setIsOpen] = useState(defaultState)
  const chevronColor = DEFAULT_THEME_COLORS['--theme-color--brand-primary']

  const handleOnClick = () => {
    setIsOpen(!isOpen)

    if (props.onCollapsibleToggle) {
      props.onCollapsibleToggle(!isOpen)
    }
  }

  return (
    <ContainerExternal
      tabIndex={disabled ? -1 : 0}
      disabled={disabled}
      role="button"
      data-id={dataId}
      color={color}
    >
      <ContainerInternal onClick={handleOnClick}>
        {iconName && (
          <div>
            <UiIcon name={iconName} />
          </div>
        )}

        <Text>
          <Label role="title">{label}</Label>
        </Text>
        <Chevron>
          <UiIcon name={isOpen ? 'chevron_up' : 'chevron_down'} color={chevronColor} />
        </Chevron>
      </ContainerInternal>
      {children && isOpen && (
        <div style={{ paddingTop: '12px', position: 'relative' }}>{children}</div>
      )}
    </ContainerExternal>
  )
}
